import { useState, useEffect, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Loader2, Users } from "lucide-react"
import { useInfiniteCommunity } from "@/hooks/useShare"
import { JustifiedRows } from "@/components/common/JustifiedRows"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"
import { CommunityPreviewModal } from "./CommunityPreviewModal"
import type { SharedVideo } from "@/types/share"

function ThumbnailRotator({ video }: { video: SharedVideo }) {
  const { t } = useTranslation()
  const thumbnails = useMemo(
    () => (video.thumbnailUrls && video.thumbnailUrls.length > 0)
      ? video.thumbnailUrls
      : (video.thumbnailUrl ? [video.thumbnailUrl] : []),
    [video.thumbnailUrls, video.thumbnailUrl],
  )

  const FADE_MS = 1000
  const HOLD_MS = 3000

  const [state, setState] = useState<{
    activeIndex: number
    incomingIndex: number | null
    isFading: boolean
    showIncoming: boolean
  }>({
    activeIndex: 0,
    incomingIndex: null,
    isFading: false,
    showIncoming: false,
  })

  // Preload all thumbnail frames so each transition can crossfade smoothly.
  useEffect(() => {
    if (thumbnails.length <= 1) return
    thumbnails.forEach((src) => {
      const img = new Image()
      img.src = src
    })
  }, [thumbnails])

  useEffect(() => {
    if (thumbnails.length <= 1) return

    const intervalId = window.setInterval(() => {
      setState((prev) => {
        if (prev.isFading) return prev
        const next = (prev.activeIndex + 1) % thumbnails.length
        return {
          activeIndex: prev.activeIndex,
          incomingIndex: next,
          isFading: false,
          showIncoming: true,
        }
      })
    }, HOLD_MS)

    return () => window.clearInterval(intervalId)
  }, [thumbnails.length, HOLD_MS])

  useEffect(() => {
    if (state.incomingIndex === null || state.showIncoming === false || state.isFading) return
    const rafId = window.requestAnimationFrame(() => {
      setState((prev) => {
        if (prev.incomingIndex === null) return prev
        return {
          ...prev,
          isFading: true,
        }
      })
    })
    return () => window.cancelAnimationFrame(rafId)
  }, [state.incomingIndex, state.showIncoming, state.isFading])

  useEffect(() => {
    if (!state.isFading || state.incomingIndex === null) return

    const timeoutId = window.setTimeout(() => {
      setState((prev) => {
        if (!prev.isFading || prev.incomingIndex === null) return prev
        return {
          activeIndex: prev.incomingIndex,
          incomingIndex: null,
          isFading: false,
          showIncoming: false,
        }
      })
    }, FADE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [state.isFading, state.incomingIndex, FADE_MS])

  if (thumbnails.length === 0) {
    return <div className="w-full h-full bg-slate-100" />
  }

  if (thumbnails.length === 1) {
    return (
      <img
        src={thumbnails[0] ?? ""}
        alt={t('community.thumbnailAlt')}
        className="w-full h-full object-cover"
        loading="eager"
      />
    )
  }

  const activeSrc = thumbnails[state.activeIndex] ?? thumbnails[0] ?? ""
  const incomingSrc = state.incomingIndex !== null
    ? (thumbnails[state.incomingIndex] ?? activeSrc)
    : ""

  return (
    <div className="relative w-full h-full">
      <img
        src={activeSrc}
        alt={t('community.thumbnailAlt')}
        className="absolute inset-0 w-full h-full object-cover transition-opacity"
        style={{
          opacity: state.isFading ? 0 : 1,
          transitionDuration: state.showIncoming ? `${FADE_MS}ms` : "0ms",
          transitionTimingFunction: "ease-in-out",
        }}
        loading="eager"
      />
      {state.showIncoming && state.incomingIndex !== null && (
        <img
          src={incomingSrc}
          alt={t('community.thumbnailAlt')}
          className="absolute inset-0 w-full h-full object-cover transition-opacity"
          style={{
            opacity: state.isFading ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
            transitionTimingFunction: "ease-in-out",
          }}
          loading="eager"
        />
      )}
    </div>
  )
}

export function CommunityTab() {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteCommunity(36)
  const observerTarget = useRef<HTMLDivElement>(null)
  const [selectedVideo, setSelectedVideo] = useState<SharedVideo | null>(null)

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <p>{t('community.loadFailed')}</p>
        <p className="text-sm text-slate-400 mt-1">{t('common.tryAgainLater')}</p>
      </div>
    )
  }

  // Flatten all pages into a single array
  const allItems = data?.pages.flatMap((page) => page.items) || []

  if (!allItems.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
          <Users className="w-10 h-10 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('community.emptyTitle')}
        </h3>
        <p className="text-slate-500 text-sm whitespace-pre-line">
          {t('community.emptyDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 좌→우 행 맞춤 레이아웃 */}
      <JustifiedRows
        items={allItems}
        getKey={(item) => item.id}
        getAspectRatio={(item) => item.ratio}
        renderItem={(item) => (
          <div
            className="cursor-pointer group"
            onClick={() => setSelectedVideo(item)}
          >
            <div
              className="relative bg-slate-100 rounded-lg overflow-hidden"
              style={{ aspectRatio: item.ratio.replace(':', '/') }}
            >
              <ThumbnailRotator key={`${item.id}-${item.thumbnailUrls?.length ?? 1}`} video={item} />
              <AiGeneratedNotice variant="badge" className="absolute left-2 top-2 z-10" />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-end p-3 opacity-0 group-hover:opacity-100 pointer-events-none">
                <p className="text-white text-sm line-clamp-2">{item.prompt}</p>
              </div>
            </div>
          </div>
        )}
      />

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div
          ref={observerTarget}
          className="flex items-center justify-center py-8"
        >
          {isFetchingNextPage && (
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          )}
        </div>
      )}

      {/* Preview Modal */}
      <CommunityPreviewModal
        video={selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />
    </div>
  )
}
